// ============================================================================
// FNE-BRIDGE - Store Stickers (Zustand)
// ============================================================================

import { create } from 'zustand';
import type { StickerTransaction, StickerDailySummary } from '../types/models';
import type { StickerFilters } from '../types/api';

// ============================================================================
// Types du state
// ============================================================================

interface StickerState {
  // Solde
  balance: number;
  balanceLoading: boolean;

  // Transactions
  transactions: StickerTransaction[];
  transactionsTotal: number;
  transactionsLoading: boolean;
  transactionsFilters: StickerFilters;

  // Consommation 7 jours
  dailySummary: StickerDailySummary[];
  dailySummaryLoading: boolean;

  // Achat en cours
  purchaseLoading: boolean;
  purchaseError: string | null;
  purchaseSuccess: boolean;
  paymentUrl: string | null;

  // Erreur générale
  error: string | null;

  // Actions
  setBalance: (balance: number) => void;
  setBalanceLoading: (loading: boolean) => void;
  setTransactions: (transactions: StickerTransaction[], total: number) => void;
  setTransactionsLoading: (loading: boolean) => void;
  setTransactionsFilters: (filters: Partial<StickerFilters>) => void;
  setDailySummary: (summary: StickerDailySummary[]) => void;
  setDailySummaryLoading: (loading: boolean) => void;
  setPurchaseLoading: (loading: boolean) => void;
  setPurchaseError: (error: string | null) => void;
  setPurchaseSuccess: (success: boolean, paymentUrl?: string | null) => void;
  setError: (error: string | null) => void;
  resetPurchase: () => void;
  reset: () => void;
}

// ============================================================================
// Store
// ============================================================================

export const useStickerStore = create<StickerState>()((set) => ({
  balance: 0,
  balanceLoading: false,
  transactions: [],
  transactionsTotal: 0,
  transactionsLoading: false,
  transactionsFilters: { page: 1, limit: 20 },
  dailySummary: [],
  dailySummaryLoading: false,
  purchaseLoading: false,
  purchaseError: null,
  purchaseSuccess: false,
  paymentUrl: null,
  error: null,

  setBalance: (balance) => set({ balance, balanceLoading: false }),
  setBalanceLoading: (balanceLoading) => set({ balanceLoading }),

  setTransactions: (transactions, transactionsTotal) =>
    set({ transactions, transactionsTotal, transactionsLoading: false }),
  setTransactionsLoading: (transactionsLoading) => set({ transactionsLoading }),
  setTransactionsFilters: (filters) =>
    set((state) => ({
      transactionsFilters: { ...state.transactionsFilters, ...filters },
    })),

  setDailySummary: (dailySummary) => set({ dailySummary, dailySummaryLoading: false }),
  setDailySummaryLoading: (dailySummaryLoading) => set({ dailySummaryLoading }),

  setPurchaseLoading: (purchaseLoading) =>
    set({ purchaseLoading, purchaseError: null, purchaseSuccess: false }),
  setPurchaseError: (purchaseError) =>
    set({ purchaseError, purchaseLoading: false, purchaseSuccess: false }),
  setPurchaseSuccess: (purchaseSuccess, paymentUrl = null) =>
    set({ purchaseSuccess, paymentUrl, purchaseLoading: false, purchaseError: null }),

  setError: (error) => set({ error }),

  resetPurchase: () =>
    set({ purchaseLoading: false, purchaseError: null, purchaseSuccess: false, paymentUrl: null }),

  reset: () =>
    set({
      balance: 0,
      balanceLoading: false,
      transactions: [],
      transactionsTotal: 0,
      transactionsLoading: false,
      transactionsFilters: { page: 1, limit: 20 },
      dailySummary: [],
      dailySummaryLoading: false,
      purchaseLoading: false,
      purchaseError: null,
      purchaseSuccess: false,
      paymentUrl: null,
      error: null,
    }),
}));
